import { SubmissionReports } from './SubmissionReports'
import { useBusinessOptions } from '@/services/businessOptions'
import { ReportCards, reportCards } from '@/components/reports/ReportCards'
import { LiveReportsScreen } from './LiveReports'
import { RuntimeStatus } from './RuntimeStatus'
import { centralEnabled } from '@/config/api';
import { CentralUsers } from './CentralAccounts';
import { useAuth } from "@/context/AuthContext";
import { currentBusinessName } from "@/helpers/businessName";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Button, Card, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faFileLines, faListCheck, faMapLocationDot, faPercent, faStar, faUserCheck, faUsers, faUserTag } from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useDemo } from "@/context/DemoContext";
import { ownerRole } from "@/helpers/roles";
import { AdminPageHero } from "@/components/common/AdminPageHero";
import { EditTableScroll } from "@/components/common/EditTableScroll";
import type { NamedItem, ReferenceKind } from "@/models/api";
import type { Core } from "@form-validation/core";
import { createFormValidation, isFormValid } from "@/helpers/formValidationHelper";
import { readError } from "@/services/apiClient";
import { deleteReferenceData, loadReferenceData, saveReferenceData } from "@/services/referenceService";
import { CustomerTable, Field, Heading, PreviewGate } from "./CrmScreens";
import { CrmAccountScreen } from "./CrmAccountScreen";
import { LiveUsersScreen } from "./UsersAdmin";
export function ReportsScreen() {
  const { isDemo } = useAuth()
  const { report } = useParams()
  if (report === 'submissions' || report === 'direct-submissions') return <SubmissionReports direct={report === 'direct-submissions'} />
  return isDemo ? <DemoReportsScreen /> : <LiveReportsScreen />
}
function DemoReportsScreen() {
  const { report } = useParams();
  const { customers, actions, entries } = useDemo();
  const [grid, setGrid] = useState("All");
  const [category, setCategory] = useState("All");
  const [owner, setOwner] = useState("All");
  const [postcode, setPostcode] = useState("");
  const [ran, setRan] = useState(false);
  const title = reportCards.find(item => item.slug === report)?.title;
  const rows = customers.filter(
    (c) =>
      (grid === "All" || c.grid === grid) &&
      (category === "All" || c.category === category) &&
      (owner === "All" || c.owner === owner) &&
      c.postcode.toLowerCase().includes(postcode.toLowerCase()),
  );
  return (
    <PreviewGate>
      <Heading
        title={title ?? "CRM reports"}
        subtitle="Explore customer accounts and your team’s activity."
      >
        {report && (
          <Link className="btn btn-outline-primary" to="/reports">
            All reports
          </Link>
        )}
      </Heading>
      {!report ? (
        <ReportCards />
      ) : (
        <>
          <Card className="mb-4">
            <Card.Body>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  setRan(true);
                }}
              >
                <Row className="g-3">
                  <Col md={3}>
                    <Field
                      label="Account owner"
                      value={owner}
                      onChange={setOwner}
                      options={["All", "Alex Morgan", "Jamie Taylor"]}
                    />
                  </Col>
                  {report === "sales" ? (
                    <>
                      <Col md={3}>
                        <Form.Label htmlFor="date-from">Date from</Form.Label>
                        <Form.Control
                          id="date-from"
                          type="date"
                          defaultValue="2026-10-01"
                          disabled
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label htmlFor="date-to">Date to</Form.Label>
                        <Form.Control
                          id="date-to"
                          type="date"
                          defaultValue="2026-10-31"
                          disabled
                        />
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col md={3}>
                        <Field
                          label="Grid reference"
                          value={grid}
                          onChange={setGrid}
                          options={["All", "A1", "A2", "B1", "B2"]}
                        />
                      </Col>
                      <Col md={3}>
                        <Field
                          label="Category"
                          value={category}
                          onChange={setCategory}
                          options={["All", "Trading", "Prospect", "Inactive"]}
                        />
                      </Col>
                      <Col md={3}>
                        <Field
                          label="Postcode"
                          value={postcode}
                          onChange={setPostcode}
                        />
                      </Col>
                    </>
                  )}
                </Row>
                {report === "last-action" && (
                  <Alert variant="info">
                    Last-contact age filtering will be connected to the API.
                    This preview shows matching sample accounts.
                  </Alert>
                )}
                {report === "sales" && (
                  <p className="text-body-secondary">
                    Sample activity totals for the demo session; date-range
                    filtering awaits the API.
                  </p>
                )}
                <Button type="submit">Run report</Button>
              </Form>
            </Card.Body>
          </Card>
          {ran &&
            (report === "sales" ? (
              <Row className="g-3">
                {["Alex Morgan", "Jamie Taylor"]
                  .filter((n) => owner === "All" || n === owner)
                  .map((n) => (
                    <Col md={6} key={n}>
                      <Card>
                        <Card.Header>{n}</Card.Header>
                        <Card.Body>
                          <p>
                            Actions added:{" "}
                            <strong>
                              {actions.filter((a) => a.owner === n).length}
                            </strong>
                          </p>
                          <p>
                            Actions completed:{" "}
                            <strong>
                              {
                                actions.filter((a) => a.owner === n && a.closed)
                                  .length
                              }
                            </strong>
                          </p>
                          <p>
                            Notes added:{" "}
                            <strong>
                              {n === "Alex Morgan"
                                ? entries.filter((e) => e.kind === "note")
                                    .length
                                : 0}
                            </strong>
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
              </Row>
            ) : (
              <Card>
                <Card.Header className="d-flex justify-content-between">
                  <span>{rows.length} matching customers</span>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => window.print()}
                  >
                    Print results
                  </Button>
                </Card.Header>
                <CustomerTable rows={rows} />
                {!rows.length && <p className="p-4">No matching customers.</p>}
              </Card>
            ))}
        </>
      )}
    </PreviewGate>
  );
}
const configTiles: { to: string; kind?: ReferenceKind; label: string; description?: string; icon: IconDefinition; group: "data" | "system"; ownerOnly?: boolean }[] = [
  { to: "/setup", label: "Guided setup", description: "Review your CRM setup plan", icon: faListCheck, group: "system" },
  { to: "/catalogue", label: "Catalogue", description: "Browse products and services, or import a catalogue", icon: faFileLines, group: "data" },
  { to: "/imports", label: "Import CSV", description: "Import customers, products and services", icon: faFileLines, group: "data" },
  { to: "/admin/grid-refs", kind: "grid-refs", label: "Grid Refs", icon: faMapLocationDot, group: "data" },
  { to: "/admin/discounts", kind: "discounts", label: "Discounts", icon: faPercent, group: "data" },
  { to: "/admin/customer-types", kind: "customer-types", label: "Customer Types", icon: faUserTag, group: "data" },
  { to: "/admin/customer-categories", kind: "customer-categories", label: "Customer Categories", icon: faStar, group: "data" },
  { to: "/admin/customer-status", kind: "customer-status", label: "Customer Status", icon: faUserCheck, group: "data" },
  { to: "/admin/title-prefixes", kind: "title-prefixes", label: "Contact Titles", icon: faUsers, group: "data" },
  { to: "/admin/contact-methods", kind: "contact-methods", label: "Contact Methods", icon: faUsers, group: "data" },
  { to: "/admin/action-types", kind: "action-types", label: "Action Types", icon: faListCheck, group: "data" },
  { to: "/admin/settings", label: "Status & Audit", icon: faFileLines, group: "system" },
  { to: "/admin/forms", label: "Forms", description: "Create and manage forms", icon: faFileLines, group: "system" },
  { to: "/admin/users", label: "Users", icon: faUsers, group: "system" },
  { to: "/admin/business-details", label: "Business Details", icon: faBuilding, group: "system", ownerOnly: true },
];
const seeds: Record<string, string[]> = {
  "grid-refs": ["A1", "A2", "B1", "B2"],
  discounts: ["0%", "5%", "10%"],
  "customer-types": ["Public house", "Hotel", "Restaurant"],
  "customer-categories": ["Prospect", "Trading", "Inactive"],
  "customer-status": ["Active", "Inactive"],
  "action-types": ["Call", "Email", "Visit"],
};
function AdminSummary({ name }: { name: string | null }) {
  return <>Data settings and system settings for {name ? <strong>{name}</strong> : "this business"}.</>;
}
function sectionTitle(section: string | undefined, tileLabel?: string) {
  if (tileLabel) return tileLabel;
  if (section === "users") return "Users";
  if (section === "business-details") return "Business Details";
  return "System Admin";
}
export function AdminScreen() {
  const { options: businessOptions } = useBusinessOptions()
  const { section } = useParams();
  const { isDemo, session } = useAuth();
  const owner = session?.role === ownerRole;
  const tile = section ? configTiles.find((item) => item.kind === section) : undefined;
  return (
    <>
      {!(section === "users" && centralEnabled && !isDemo) && <AdminPageHero
        title={sectionTitle(section, tile?.label)}
        summary={section === "business-details" ? "Business details and account options." : section ? "Changes are saved straight away." : <AdminSummary name={currentBusinessName(session)} />}
      >
        {section && (
          <Link className="btn btn-outline-primary" to="/admin">
            All settings
          </Link>
        )}
      </AdminPageHero>}
      {!section && (
        <>
          <section className="config-group">
            <h2>Data Settings</h2>
            <TileGrid tiles={configTiles.filter((item) => item.group === "data" && (businessOptions.useGridReferences || item.kind !== "grid-refs"))} />
          </section>
          <section className="config-group">
            <h2>System Settings</h2>
            <TileGrid tiles={configTiles.filter((item) => item.group === "system" && (!item.ownerOnly || owner))} />
          </section>
        </>
      )}
      {tile?.kind === "grid-refs" && !businessOptions.useGridReferences && <Alert variant="info">Grid references are turned off for this business.</Alert>}
      {tile?.kind && (tile.kind !== "grid-refs" || businessOptions.useGridReferences) && (isDemo ? <ReferenceEditor key={tile.kind} section={tile.kind} /> : <LiveReferenceEditor key={tile.kind} kind={tile.kind} />)}
      {!tile && section === "users" && (isDemo ? <UsersEditor /> : centralEnabled ? <CentralUsers /> : <LiveUsersScreen />)}
      {!tile && section === "business-details" && (owner ? (isDemo ? <Alert variant="info">Business details are available in the live workspace.</Alert> : <CrmAccountScreen />) : <Alert variant="info">Only the account owner can change these details.</Alert>)}
      {!tile && section === "settings" && (
        <Card>
          <Card.Header>Runtime settings</Card.Header>
          <Card.Body>
            {isDemo ? <Alert variant="info">Runtime status is available in the live workspace.</Alert> : <RuntimeStatus />}
          </Card.Body>
        </Card>
      )}
    </>
  );
}
function TileGrid({ tiles }: { tiles: typeof configTiles }) {
  return (
    <div className="config-tiles">
      {tiles.map((item) => (
        <Link key={item.to} className="config-tile" to={item.to}>
          <FontAwesomeIcon icon={item.icon} />
          <span>{item.label}{item.description && <small className="d-block text-body-secondary fw-normal">{item.description}</small>}</span>
        </Link>
      ))}
    </div>
  );
}
const requiredMessages: Record<string, string> = {
  "action-types": "Action type required",
  "customer-types": "Customer type required",
  "customer-categories": "Customer category required",
  "customer-status": "Customer status required",
  discounts: "Discount required",
  "grid-refs": "Grid ref required",
};
function ReferenceValueForm({
  value,
  editing,
  busy = false,
  requiredMessage,
  onChange,
  onSubmit,
  onCancel,
}: {
  value: string;
  editing: boolean;
  busy?: boolean;
  requiredMessage: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const fvRef = useRef<Core | null>(null);

  useEffect(() => {
    const element = formRef.current;
    if (!element) return;
    const fv = createFormValidation(element, {
      submitOnly: true,
      fields: {
        value: {
          validators: {
            callback: {
              message: requiredMessage,
              callback: (input: { value?: unknown }) => String(input.value ?? "").trim() !== "",
            },
          },
        },
      },
    });
    fvRef.current = fv;
    return () => {
      fv.destroy();
      fvRef.current = null;
    };
  }, [requiredMessage]);

  useEffect(() => {
    fvRef.current?.resetForm();
  }, [editing]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!(await isFormValid(fvRef.current))) return;
    onSubmit();
    fvRef.current?.resetForm();
  }

  return (
    <Form ref={formRef} noValidate onSubmit={(event) => void submit(event)}>
      <Form.Group className="mb-3">
        <Form.Label>Value</Form.Label>
        <InputGroup className="reference-value-group">
          <Form.Control name="value" value={value} onChange={(event) => onChange(event.target.value)} />
          <Button type="submit" disabled={busy}>{editing ? "Save changes" : "Add value"}</Button>
          {editing && (
            <Button type="button" variant="outline-secondary" disabled={busy} onClick={() => { fvRef.current?.resetForm(); onCancel(); }}>
              Cancel
            </Button>
          )}
        </InputGroup>
      </Form.Group>
    </Form>
  );
}
function LiveReferenceEditor({ kind }: { kind: ReferenceKind }) {
  const [rows, setRows] = useState<NamedItem[]>([]);
  const [value, setValue] = useState("");
  const [editing, setEditing] = useState<NamedItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const showEnabled = kind !== "grid-refs";
  const readOnly = kind === "customer-status";
  const ordered = ["customer-categories", "action-types", "contact-methods"].includes(kind);
  const [methodType, setMethodType] = useState(1);
  const [percentage, setPercentage] = useState<string>("");
  const [sortOrder, setSortOrder] = useState(0);

  function reload() {
    setLoading(true);
    return loadReferenceData(kind)
      .then((items) => { setRows(items); setError(null); })
      .catch((caught) => setError(readError(caught)))
      .finally(() => setLoading(false));
  }

  useEffect(() => { void reload(); }, [kind]);

  async function persist(item: NamedItem | { name: string; enabled: boolean; order: number; methodType?: number | null; percentage?: number | null }) {
    setBusy(true);
    setError(null);
    try {
      await saveReferenceData(kind, { id: "id" in item ? item.id : null, name: item.name, enabled: item.enabled, order: item.order, methodType: item.methodType, percentage: item.percentage });
      setValue(""); setPercentage("");
      setEditing(null);
      await reload();
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function remove(item: NamedItem) {
    setBusy(true);
    setError(null);
    try {
      await deleteReferenceData(kind, item.id);
      await reload();
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? <p>Loading…</p> : (
          <>
            {readOnly && <Alert variant="info">Customer statuses are centrally managed and shared across businesses.</Alert>}
            {!readOnly && <>
            {kind === 'contact-methods' && <Form.Group className="mb-3"><Form.Label>Contact method type</Form.Label><Form.Select value={methodType} onChange={e => setMethodType(Number(e.target.value))}><option value={1}>Phone</option><option value={2}>Email</option></Form.Select></Form.Group>}
            {kind === 'discounts' && <Form.Group controlId="discount-percentage" className="mb-3"><Form.Label>Catalogue discount percentage</Form.Label><Form.Control type="number" min="0" max="100" step="0.0001" value={percentage} onChange={e => setPercentage(e.target.value)} /><Form.Text>Set a numeric percentage, including 0. Existing labels are not interpreted as percentages. Leave blank to keep an existing rate.</Form.Text></Form.Group>}
            {ordered && <Form.Group className="mb-3"><Form.Label>Sort order</Form.Label><Form.Control type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} /></Form.Group>}
            <ReferenceValueForm
              value={value}
              editing={editing !== null}
              busy={busy}
              requiredMessage={requiredMessages[kind] ?? "Value required"}
              onChange={setValue}
              onSubmit={() => {
                const name = value.trim();
                void persist(editing ? { ...editing, name, order: sortOrder, methodType, percentage: percentage === "" ? undefined : Number(percentage) } : { name, enabled: true, order: sortOrder, methodType, percentage: percentage === "" ? undefined : Number(percentage) });
              }}
              onCancel={() => { setEditing(null); setValue(""); setPercentage(""); }}
            />
            </>}
            <EditTableScroll>
            <Table striped>
              <thead>
                <tr><th>Value</th>{showEnabled && <th>Enabled</th>}<th></th></tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}{kind === "discounts" && <small className="ms-2">{row.percentage == null ? "Numeric rate not configured" : `${row.percentage}% for catalogue orders`}</small>}{ordered && <small className="ms-2 text-secondary">Order: {row.order}</small>}{kind === "contact-methods" && <small className="ms-2">{row.methodType === 2 ? "Email" : "Phone"}</small>}</td>
                    {showEnabled && (
                      <td>
                        <Form.Check aria-label={`Enable ${row.name}`} checked={row.enabled} disabled={busy || readOnly} onChange={() => void persist({ ...row, enabled: !row.enabled })} />
                      </td>
                    )}
                    <td className="text-nowrap">
                      <Button variant="outline-primary" size="sm" disabled={readOnly} onClick={() => { setEditing(row); setValue(row.name); setPercentage(row.percentage == null ? "" : String(row.percentage)); setMethodType(row.methodType ?? 1); setSortOrder(row.order); }}>Edit</Button>{" "}
                      <Button variant="outline-danger" size="sm" disabled={busy || readOnly} onClick={() => void remove(row)}>Remove</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </EditTableScroll>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
function ReferenceEditor({ section }: { section: string }) {
  const { session } = useAuth();
  const storageKey = `reference-${section}${session?.businessId === "demo" ? "" : `:${session?.businessId}`}`;
  const [rows, setRows] = useState<
    {
      name: string;
      enabled: boolean;
    }[]
  >(() => {
    try {
      return (
        JSON.parse(sessionStorage.getItem(storageKey) || "null") ??
        (seeds[section] ?? []).map((name) => ({ name, enabled: true }))
      );
    } catch {
      return [];
    }
  });
  const [value, setValue] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  function save(next: typeof rows) {
    setRows(next);
    sessionStorage.setItem(storageKey, JSON.stringify(next));
  }
  return (
    <Card>
      <Card.Body>
        <ReferenceValueForm
          value={value}
          editing={editing !== null}
          requiredMessage={requiredMessages[section] ?? "Value required"}
          onChange={setValue}
          onSubmit={() => {
            const name = value.trim();
            if (editing === null) save([...rows, { name, enabled: true }]);
            else save(rows.map((row, index) => (index === editing ? { ...row, name } : row)));
            setValue("");
            setEditing(null);
          }}
          onCancel={() => { setEditing(null); setValue(""); }}
        />
        <EditTableScroll>
        <Table striped>
          <thead>
            <tr>
              <th>Value</th>
              <th>Enabled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.name}</td>
                <td>
                  <Form.Check
                    aria-label={`Enable ${r.name}`}
                    checked={r.enabled}
                    onChange={() =>
                      save(
                        rows.map((x, j) =>
                          j === i ? { ...x, enabled: !x.enabled } : x,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      setEditing(i);
                      setValue(r.name);
                    }}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        </EditTableScroll>
        <p className="small text-body-secondary">
          Preview reference values are stored locally; customer dropdowns
          currently use the initial sample values.
        </p>
      </Card.Body>
    </Card>
  );
}
function UsersEditor() {
  const { customers, actions, setCustomers, setActions } = useDemo();
  const [from, setFrom] = useState("Alex Morgan");
  const [to, setTo] = useState("Jamie Taylor");
  const [done, setDone] = useState(false);
  return (
    <Card>
      <Card.Header>Team users</Card.Header>
      <Card.Body>
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Alex Morgan</td>
              <td>alex@example.test</td>
              <td>Business admin</td>
              <td>Active</td>
            </tr>
            <tr>
              <td>Jamie Taylor</td>
              <td>jamie@example.test</td>
              <td>Editor</td>
              <td>Active</td>
            </tr>
          </tbody>
        </Table>
        <h3 className="h5 mt-4">Reassign customer accounts and open actions</h3>
        {done && (
          <Alert variant="success">
            Demo accounts and open actions reassigned.
          </Alert>
        )}
        <Row>
          <Col md={5}>
            <Field
              label="From user"
              value={from}
              onChange={(v) => {
                setFrom(v);
                setDone(false);
              }}
              options={["Alex Morgan", "Jamie Taylor"]}
            />
          </Col>
          <Col md={5}>
            <Field
              label="To user"
              value={to}
              onChange={(v) => {
                setTo(v);
                setDone(false);
              }}
              options={["Alex Morgan", "Jamie Taylor"]}
            />
          </Col>
        </Row>
        <Button
          disabled={from === to}
          onClick={() => {
            setCustomers(
              customers.map((c) =>
                c.owner === from ? { ...c, owner: to } : c,
              ),
            );
            setActions(
              actions.map((a) =>
                a.owner === from && !a.closed ? { ...a, owner: to } : a,
              ),
            );
            setDone(true);
          }}
        >
          Reassign demo records
        </Button>
      </Card.Body>
    </Card>
  );
}
